<template>
  <e-button
    :class="b('', { variant })"
    :aria-label="label"
    @click.stop.prevent="addFavorites"
  >
    <span>
      {{ label }}
    </span>
  </e-button>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import { FAVORITES_FEATURE_MAX_ITEMS_LIMIT } from '@/setup/globals';
  import useFavoritesStore from '@/stores/favorites';
  import eButton from '@/elements/e-button.vue';

  type Setup = {
    favoritesStore: ReturnType<typeof useFavoritesStore>;
  };
  // type Data = {};

  /**
   * Renders a button to add and remove a product to and from the favorites store.
   */
  export default defineComponent({
    name: 'c-add-filter-result-to-favorites',
    components: {
      eButton,
    },

    // components: {},

    props: {
      /**
       * Define the variant of the button
       */
      variant: {
        type: String,
        default: 'regular',
        validator: (value: string) => ['regular', 'small'].includes(value),
      },

      /**
       * The products to add to the favorites store
       */
      productIds: {
        type: Array as PropType<number[]>,
        required: true,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        favoritesStore: useFavoritesStore(),
      };
    },
    // data(): Data {
    //   return {};
    // },

    computed: {
      label(): string {
        return this.$t('c-add-filter-result-to-favorites.add-filter-results');
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      async addFavorites(): Promise<void> {
        const limitReachedMessage = this.$t('c-add-filter-result-to-favorites.would-exceed-limit');

        if (!this.productIds.length) {
          this.favoritesStore.showNotification(limitReachedMessage);

          return;
        }

        const existingFavorites = this.favoritesStore.favorites || [];
        const existingCount = existingFavorites.length;
        const existingIds = new Set(existingFavorites.map((favorite) => favorite.id));

        // Only consider IDs that are not already in favorites
        const uniqueNewIds = (this.productIds || []).filter((id) => !existingIds.has(id));

        // Nothing to add
        if (uniqueNewIds.length === 0) {
          this.favoritesStore.showNotification(
            this.$t('c-add-filter-result-to-favorites.nothing-to-add-message'),
            'info',
          );

          return;
        }

        // Check limit using only unique, truly new IDs
        if (existingCount + uniqueNewIds.length > FAVORITES_FEATURE_MAX_ITEMS_LIMIT) {
          this.favoritesStore.showNotification(limitReachedMessage);

          return;
        }

        // Fetch only new IDs
        await this.favoritesStore.apiFetchFavoritesByIds(uniqueNewIds);

        const fetched = this.favoritesStore.filterResultFavorites || [];

        if (!fetched.length) {
          return;
        }

        // Double-check with actual fetched items and avoid duplicates
        const toAdd = fetched.filter((fav) => !existingIds.has(fav.id));

        if (existingCount + toAdd.length > FAVORITES_FEATURE_MAX_ITEMS_LIMIT) {
          this.favoritesStore.showNotification(limitReachedMessage);

          return;
        }

        toAdd.forEach((favorite) =>
          this.favoritesStore.addFavorite(favorite, this.$t('c-add-to-favorites.limit-reached')),
        );
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-add-filter-results-to-favorites {
    &--variant-small {
      font-size: 0.75rem;
    }

    .e-button__inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.25rem;
    }
  }
</style>
